/**
 * AdminUsersPage - /admin/users (SPEC sections 5 + 9).
 *
 * Closes Phase 17 audit gap G1: the backend shipped admin-user CRUD
 * endpoints but no UI to drive them existed. This page is the missing
 * surface.
 *
 * Layout:
 *   - Header with breadcrumb + Add-user button.
 *   - Filter bar: search (q) + role select + status select + include-deleted toggle.
 *   - Table: Email | Name | Role | Status | MFA | Created | Actions.
 *   - Pager (prev / next + "Page N of M").
 *
 * Row click opens an edit modal that wraps PUT /admin/users/{id} for
 * the name/role/active diff and PUT /admin/users/{id}/permissions for
 * the can_see_company_tickets toggle (the backend split these on
 * purpose so the audit-log entries stay distinct).
 *
 * Filters persist to localStorage so navigating away and back lands on
 * the same view - mirroring the agent-inbox pattern called out in
 * SPEC section 9.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Spinner,
} from "@/components/ui";
import {
  extractDetailMessage,
  useAdminUsers,
  useCreateAdminUser,
  useDeleteAdminUser,
  useResendSetupEmail,
  useUpdateAdminUser,
  useUpdateAdminUserPermissions,
  type AdminUsersFilters,
} from "@/hooks/useAdminUsers";
import { usePageTitle } from "@/hooks/usePageTitle";
import type {
  AdminUserResponse,
  AdminUserUpdateRequest,
  SystemRole,
} from "@/types/api";

const FILTERS_STORAGE_KEY = "buyist-support:admin-users-filters";
const PAGE_SIZE = 50;

interface PersistedFilters {
  q: string;
  role: "" | SystemRole;
  status: "" | "active" | "inactive";
  includeDeleted: boolean;
}

const DEFAULT_FILTERS: PersistedFilters = {
  q: "",
  role: "",
  status: "",
  includeDeleted: false,
};

const ROLE_FILTER_OPTIONS: { value: "" | SystemRole; label: string }[] = [
  { value: "", label: "All roles" },
  { value: "customer", label: "Customer" },
  { value: "agent", label: "Agent" },
  { value: "system_admin", label: "System admin" },
];

const STATUS_FILTER_OPTIONS: {
  value: "" | "active" | "inactive";
  label: string;
}[] = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const ROLE_FORM_OPTIONS: { value: SystemRole; label: string }[] = [
  { value: "customer", label: "Customer" },
  { value: "agent", label: "Agent" },
  { value: "system_admin", label: "System admin" },
];

function readPersistedFilters(): PersistedFilters {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<PersistedFilters>;
    return {
      q: typeof parsed.q === "string" ? parsed.q : "",
      role: (parsed.role as "" | SystemRole) ?? "",
      status: (parsed.status as "" | "active" | "inactive") ?? "",
      includeDeleted: Boolean(parsed.includeDeleted),
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

function writePersistedFilters(filters: PersistedFilters): void {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    /* noop - storage may be disabled in private browsing */
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

function RoleBadge({ role }: { role: SystemRole }): JSX.Element {
  if (role === "system_admin") {
    return (
      <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
        System admin
      </span>
    );
  }
  if (role === "agent") {
    return <Badge variant="info">Agent</Badge>;
  }
  return <Badge variant="default">Customer</Badge>;
}

function StatusBadge({
  user,
}: {
  user: AdminUserResponse;
}): JSX.Element {
  if (user.deleted_at) return <Badge variant="error">Deleted</Badge>;
  if (!user.is_active) return <Badge variant="warning">Inactive</Badge>;
  return <Badge variant="success">Active</Badge>;
}

// --- Edit modal ------------------------------------------------------------

interface EditModalProps {
  user: AdminUserResponse;
  isOpen: boolean;
  onClose: () => void;
}

function EditUserModal({ user, isOpen, onClose }: EditModalProps): JSX.Element {
  const update = useUpdateAdminUser();
  const updatePermissions = useUpdateAdminUserPermissions();
  const resend = useResendSetupEmail();
  const del = useDeleteAdminUser();

  const [fullName, setFullName] = useState(user.full_name);
  const [role, setRole] = useState<SystemRole>(user.role);
  const [isActive, setIsActive] = useState(user.is_active);
  const [canSeeCompany, setCanSeeCompany] = useState(
    user.can_see_company_tickets,
  );
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Reset the form state whenever a different user is opened. Without
  // this the modal would otherwise show stale values when the user
  // closes it, picks a different row, and reopens.
  useEffect(() => {
    setFullName(user.full_name);
    setRole(user.role);
    setIsActive(user.is_active);
    setCanSeeCompany(user.can_see_company_tickets);
    setError(null);
    setFeedback(null);
    setConfirmDelete(false);
  }, [user]);

  const handleSave = async () => {
    setError(null);
    setFeedback(null);
    const body: AdminUserUpdateRequest = {};
    if (fullName !== user.full_name) body.full_name = fullName;
    if (role !== user.role) body.role = role;
    if (isActive !== user.is_active) body.is_active = isActive;

    const permissionChanged =
      canSeeCompany !== user.can_see_company_tickets;

    try {
      if (Object.keys(body).length > 0) {
        await update.mutateAsync({ id: user.id, body });
      }
      if (permissionChanged) {
        await updatePermissions.mutateAsync({
          id: user.id,
          body: { can_see_company_tickets: canSeeCompany },
        });
      }
      if (Object.keys(body).length === 0 && !permissionChanged) {
        setFeedback("No changes to save.");
        return;
      }
      onClose();
    } catch (err) {
      setError(extractDetailMessage(err, "Could not save changes."));
    }
  };

  const handleResend = async () => {
    setError(null);
    setFeedback(null);
    try {
      await resend.mutateAsync(user.id);
      setFeedback("Setup link sent.");
    } catch (err) {
      setError(extractDetailMessage(err, "Could not resend setup link."));
    }
  };

  const handleConfirmDelete = async () => {
    setError(null);
    setFeedback(null);
    try {
      await del.mutateAsync(user.id);
      setConfirmDelete(false);
      onClose();
    } catch (err) {
      setError(extractDetailMessage(err, "Could not delete user."));
      setConfirmDelete(false);
    }
  };

  const isDeleted = user.deleted_at !== null;
  const saveBusy = update.isPending || updatePermissions.isPending;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Edit user">
        <div className="space-y-4">
          {error && (
            <Alert variant="error" title="Could not save">
              {error}
            </Alert>
          )}
          {feedback && <Alert variant="success">{feedback}</Alert>}

          <div>
            <label className="block text-sm font-medium text-neutral-700">
              Email
            </label>
            <p className="mt-1 break-all rounded-md bg-neutral-50 px-3 py-2 font-mono text-sm text-neutral-800">
              {user.email}
            </p>
          </div>

          <Input
            id="edit-user-name"
            label="Full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />

          <Select
            id="edit-user-role"
            label="Role"
            options={ROLE_FORM_OPTIONS}
            value={role}
            onChange={(e) => setRole(e.target.value as SystemRole)}
          />

          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Active</span>
          </label>

          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={canSeeCompany}
              onChange={(e) => setCanSeeCompany(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Can see company tickets</span>
          </label>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleResend()}
                isLoading={resend.isPending}
                disabled={isDeleted}
              >
                Resend setup link
              </Button>
              {isDeleted ? (
                <span className="text-xs text-neutral-500">
                  User is deleted
                </span>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 active:bg-red-800"
                  onClick={() => setConfirmDelete(true)}
                >
                  Soft delete
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => void handleSave()}
                isLoading={saveBusy}
              >
                Save changes
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Soft delete user?"
      >
        <p className="text-sm text-neutral-700">
          This deactivates{" "}
          <span className="font-mono">{user.email}</span> and revokes their
          active sessions and refresh tokens. The user record is preserved
          for audit history.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setConfirmDelete(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="bg-red-600 hover:bg-red-700 active:bg-red-800"
            onClick={() => void handleConfirmDelete()}
            isLoading={del.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}

// --- Create modal ----------------------------------------------------------

interface CreateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function CreateUserModal({ isOpen, onClose }: CreateModalProps): JSX.Element {
  const create = useCreateAdminUser();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<SystemRole>("customer");
  const [sendSetup, setSendSetup] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wipe form state when the modal closes so the next open is a clean
  // slate.
  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setFullName("");
      setRole("customer");
      setSendSetup(true);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    const trimmedName = fullName.trim();
    if (!trimmedEmail) {
      setError("Email is required.");
      return;
    }
    if (!trimmedName) {
      setError("Full name is required.");
      return;
    }
    try {
      await create.mutateAsync({
        email: trimmedEmail,
        full_name: trimmedName,
        role,
        send_setup_email: sendSetup,
      });
      onClose();
    } catch (err) {
      setError(extractDetailMessage(err, "Could not create user."));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add user">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert variant="error" title="Could not create user">
            {error}
          </Alert>
        )}

        <Input
          id="create-user-email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <Input
          id="create-user-name"
          label="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
        />

        <Select
          id="create-user-role"
          label="Role"
          options={ROLE_FORM_OPTIONS}
          value={role}
          onChange={(e) => setRole(e.target.value as SystemRole)}
        />

        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={sendSetup}
            onChange={(e) => setSendSetup(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          <span>Send password setup email</span>
        </label>

        <div className="flex justify-end gap-2 border-t border-neutral-200 pt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            isLoading={create.isPending}
          >
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// --- Page ------------------------------------------------------------------

export function AdminUsersPage(): JSX.Element {
  usePageTitle("Users");

  const [filters, setFilters] = useState<PersistedFilters>(() =>
    readPersistedFilters(),
  );
  const [page, setPage] = useState(1);

  // Persist filter selections; resetting page to 1 lives at the change
  // sites so a user paging through results does not lose their place
  // on every render.
  useEffect(() => {
    writePersistedFilters(filters);
  }, [filters]);

  // Debounce the free-text search so each keystroke does not fire a
  // backend call. 300ms matches the agent inbox.
  const [debouncedQ, setDebouncedQ] = useState(filters.q);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(filters.q);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filters.q]);

  const queryFilters = useMemo<AdminUsersFilters>(() => {
    const f: AdminUsersFilters = { page, page_size: PAGE_SIZE };
    const q = debouncedQ.trim();
    if (q.length > 0) f.q = q;
    if (filters.role) f.role = filters.role;
    if (filters.status === "active") f.is_active = true;
    if (filters.status === "inactive") f.is_active = false;
    if (filters.includeDeleted) f.include_deleted = true;
    return f;
  }, [debouncedQ, filters.role, filters.status, filters.includeDeleted, page]);

  const list = useAdminUsers(queryFilters);
  const [selectedUser, setSelectedUser] = useState<AdminUserResponse | null>(
    null,
  );
  const [createOpen, setCreateOpen] = useState(false);

  const totalPages = useMemo(() => {
    const total = list.data?.total ?? 0;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [list.data?.total]);

  const items = list.data?.items ?? [];

  return (
    <div className="mx-auto w-full max-w-6xl">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500">
            Admin / Users
          </p>
          <h1 className="text-2xl font-semibold text-neutral-900">Users</h1>
          <p className="mt-1 text-sm text-neutral-600">
            View, invite, and manage every account in Buyist Support.
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => setCreateOpen(true)}
        >
          <i className="fa-duotone fa-thin fa-plus" aria-hidden="true" />
          Add user
        </Button>
      </header>

      <Card padding="md" className="mb-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <Input
            id="admin-users-search"
            label="Search"
            placeholder="Email or name"
            value={filters.q}
            onChange={(e) => {
              setFilters((f) => ({ ...f, q: e.target.value }));
              setPage(1);
            }}
          />
          <Select
            id="admin-users-role"
            label="Role"
            options={ROLE_FILTER_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            value={filters.role}
            onChange={(e) => {
              setFilters((f) => ({
                ...f,
                role: e.target.value as "" | SystemRole,
              }));
              setPage(1);
            }}
          />
          <Select
            id="admin-users-status"
            label="Status"
            options={STATUS_FILTER_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            value={filters.status}
            onChange={(e) => {
              setFilters((f) => ({
                ...f,
                status: e.target.value as "" | "active" | "inactive",
              }));
              setPage(1);
            }}
          />
          <label className="flex items-end pb-1 text-sm text-neutral-700">
            <input
              id="admin-users-include-deleted"
              type="checkbox"
              checked={filters.includeDeleted}
              onChange={(e) => {
                setFilters((f) => ({
                  ...f,
                  includeDeleted: e.target.checked,
                }));
                setPage(1);
              }}
              className="mr-2 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Include deleted users</span>
          </label>
        </div>
      </Card>

      {list.isError && (
        <Alert variant="error" title="Could not load users" className="mb-4">
          {list.error instanceof Error
            ? list.error.message
            : "Try refreshing the page."}
        </Alert>
      )}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Email
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Role
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Status
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  MFA
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Created
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 bg-white">
              {list.isLoading && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center">
                    <Spinner size="md" />
                  </td>
                </tr>
              )}
              {!list.isLoading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-sm text-neutral-500"
                  >
                    No users match the current filters.
                  </td>
                </tr>
              )}
              {items.map((user) => (
                <tr
                  key={user.id}
                  onClick={() => setSelectedUser(user)}
                  className="cursor-pointer hover:bg-neutral-50"
                  data-testid="admin-user-row"
                >
                  <td className="px-3 py-3 font-mono text-xs text-neutral-800">
                    {user.email}
                  </td>
                  <td className="px-3 py-3 text-sm text-neutral-900">
                    {user.full_name}
                  </td>
                  <td className="px-3 py-3">
                    <RoleBadge role={user.role} />
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge user={user} />
                  </td>
                  <td className="px-3 py-3 text-xs text-neutral-700">
                    {user.mfa_enabled ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <i
                          className="fa-solid fa-check"
                          aria-hidden="true"
                        />
                        Enabled
                      </span>
                    ) : (
                      <span className="text-neutral-500">Off</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-neutral-500">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUser(user);
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4 flex items-center justify-between text-sm text-neutral-600">
        <span>
          Page {list.data?.page ?? page} of {totalPages}
          {list.data ? ` - ${list.data.total} total` : ""}
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || list.isLoading}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || list.isLoading}
          >
            Next
          </Button>
        </div>
      </div>

      {selectedUser && (
        <EditUserModal
          user={selectedUser}
          isOpen={selectedUser !== null}
          onClose={() => setSelectedUser(null)}
        />
      )}

      <CreateUserModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}

export default AdminUsersPage;
